import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  darken,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  CategorizationMap,
  plantProfileRoleI18nKey,
} from "portal/utils/categoryCollection/category";
import { CategorizedImageExpandedView } from "./CategorizedImageExpandedView";
import { CategorizedImagesGrid } from "./CategorizedImagesGrid";
import { classes, STALE_CHIP_COLOR, theme } from "portal/theme/theme";
import { Dimensions } from "portal/utils/categoryCollection/image";
import { getStringSetDiff } from "portal/utils/strings";
import { ImageRightClickMenu } from "./ImageRightClickMenu";
import { PlantProfileRoleIcon } from "../PlantProfileRoleIcon";
import {
  PredictionPoint,
  SessionStatus,
} from "protos/veselka/prediction_point";
import { RadioChipOption } from "portal/components/RadioChips";
import { Role, roleToJSON } from "protos/robot_syncer/plant_profile";
import {
  StyleChange,
  ValueChange,
} from "portal/components/categoryCollectionProfile/history/ValueChange";
import { useCategorizedImageData } from "./useCategorizedImageData";
import { useExpandedImageNavigation } from "./useExpandedImageNavigation";
import { useTranslation } from "react-i18next";
import {
  valueHistory,
  ValueHistory,
} from "portal/components/categoryCollectionProfile/history/versionShootTime";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import DifferenceIcon from "@mui/icons-material/Difference";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import React, { FC, ReactNode, useCallback, useMemo, useState } from "react";

export type CategorizedImagesVersus = {
  name?: string;
  chipIds?: string[];
  categoryChange?: "added" | "removed";
  shootTimeChangeCounts?: { added: number; removed: number };
  role?: Role;
};

interface CategorizedImagesProps {
  children?: ReactNode;
  categoryOptions: RadioChipOption[];
  categorizationMap: CategorizationMap;
  categoryId?: string;
  categoryName?: string;
  categoryRole?: Role;
  predictionPointIds: string[];
  dimensions: Dimensions;
  onRemove?: (predictionPointId: string) => void;
  onClassify?: (predictionPointId: string, categoryId: string) => void;
  classificationAction?: "reclassify" | "revive";
  session?: {
    processing: boolean;
    stale: boolean;
    status?: SessionStatus;
  };
  versus?: CategorizedImagesVersus;
  rightClickMenuTitle?: string;
  isRightClickDisabled?: (predictionPointId: string) => boolean;
  variant?: "accordion" | "flat";
  showImages?: boolean;
  onRequestEditCategory?: () => void;
  onRequestDeleteCategory?: () => void;
  onRequestAddToLibrary?: () => void;
}

export const CategorizedImages: FC<CategorizedImagesProps> = ({
  children,
  categoryOptions,
  categorizationMap,
  categoryId,
  categoryName,
  categoryRole,
  predictionPointIds = [],
  dimensions,
  onRemove,
  onClassify,
  classificationAction,
  session,
  versus,
  rightClickMenuTitle,
  isRightClickDisabled,
  variant = "accordion",
  showImages = true,
  onRequestEditCategory,
  onRequestDeleteCategory,
  onRequestAddToLibrary,
}) => {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | undefined>(
    undefined
  );

  const chipDiff = useMemo(
    () =>
      versus?.chipIds
        ? getStringSetDiff(versus.chipIds, predictionPointIds)
        : undefined,
    [predictionPointIds, versus?.chipIds]
  );
  const roleDiff = useMemo(
    () =>
      categoryRole !== undefined || versus?.role !== undefined
        ? valueHistory(versus?.role, categoryRole, versus !== undefined)
        : undefined,
    [categoryRole, versus]
  );

  const [isContextMenuOpen, setIsContextMenuOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<
    | {
        predictionPointId: string;
        mouseX: number;
        mouseY: number;
      }
    | undefined
  >();

  const renderedPredictionPoints = chipDiff?.removed.length
    ? [...predictionPointIds, ...chipDiff.removed]
    : predictionPointIds;
  const {
    expandedImage,
    setExpandedImage,
    orderedCombinedData,
    loadMoreRef,
    hasMore,
    loadMore,
  } = useCategorizedImageData(renderedPredictionPoints, categoryId);

  const { onNext, onBack } = useExpandedImageNavigation(
    orderedCombinedData,
    expandedImage,
    setExpandedImage,
    categoryId
  );

  const category = useMemo(() => {
    return categoryOptions.find((c) => c.id === categoryId);
  }, [categoryId, categoryOptions]);
  const isCategoryRemoved = versus?.categoryChange === "removed";
  const categoryColor = isCategoryRemoved
    ? {
        bg: darken(STALE_CHIP_COLOR.bg, 0.3),
        text: darken(theme.colors.white, 0.15),
      }
    : category?.color;
  const currentName = category?.name ?? categoryName;
  const renamedFromCategoryName =
    versus?.name && versus.name !== currentName ? versus.name : undefined;
  // a removed category only exists in the compared version, so its name is a "before" value
  const beforeCategoryName = isCategoryRemoved
    ? versus.name ?? currentName
    : renamedFromCategoryName;
  const afterCategoryName = isCategoryRemoved ? undefined : currentName;

  const hasChips = orderedCombinedData.length > 0;
  const hasFooter = Boolean(children);
  const isFooterStackedUnderImages =
    variant === "accordion" && showImages && hasChips && hasFooter;
  const hasDetails = (showImages && hasChips) || hasFooter;
  const hasCategoryMenu = Boolean(
    onRequestEditCategory || onRequestDeleteCategory || onRequestAddToLibrary
  );
  const [expanded, setExpanded] = useState(hasDetails);
  const bgColor = categoryColor?.bg ?? theme.colors.carbon.gray.light;
  const textColor = categoryColor?.text ?? theme.colors.white;
  const imageCount = isCategoryRemoved
    ? renderedPredictionPoints.length
    : predictionPointIds.length;

  const isDisabled = useMemo(
    () =>
      Boolean(
        isRightClickDisabled &&
          contextMenu?.predictionPointId &&
          isRightClickDisabled(contextMenu.predictionPointId)
      ),
    [contextMenu?.predictionPointId, isRightClickDisabled]
  );

  const renderFooter = (): ReactNode => {
    if (!isFooterStackedUnderImages) {
      return children;
    }
    return (
      <div className="[&>.MuiPaper-root]:rounded-t-none [&>.MuiAlert-root]:rounded-t-none">
        {children}
      </div>
    );
  };

  const handleThumbnailDoubleClick = useCallback(
    (index: number, data: PredictionPoint) => {
      setExpandedImage({
        index,
        data,
        editableCategoryId: categoryId,
      });
    },
    [categoryId, setExpandedImage]
  );

  const handleThumbnailRightClick = useCallback(
    (predictionPointId: string, { x, y }: { x: number; y: number }) => {
      setContextMenu({
        predictionPointId,
        mouseX: x,
        mouseY: y,
      });
      setIsContextMenuOpen(true);
    },
    [setContextMenu]
  );

  return (
    <>
      <CategorizedImageExpandedView
        expandedImage={expandedImage}
        setExpandedImage={setExpandedImage}
        onNext={onNext}
        onBack={onBack}
        categoryOptions={categoryOptions}
        categorizationMap={categorizationMap}
        onClassify={
          onClassify && classificationAction === "reclassify"
            ? onClassify
            : undefined
        }
        session={session}
      />
      {onClassify && classificationAction && (
        <ImageRightClickMenu
          title={rightClickMenuTitle}
          open={isContextMenuOpen}
          onClose={() => setIsContextMenuOpen(false)}
          onExited={() => setContextMenu(undefined)}
          anchorPosition={
            contextMenu
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          categoryOptions={categoryOptions}
          onOptionClick={(nextCategoryId: string) => {
            if (contextMenu) {
              onClassify(contextMenu.predictionPointId, nextCategoryId);
            }
          }}
          disabled={classificationAction === "revive" && isDisabled}
          disabledMessage={t(
            "components.categoryCollectionProfile.actions.inProfile"
          )}
          disabledOptionIds={
            classificationAction === "reclassify" && categoryId
              ? [categoryId]
              : undefined
          }
        />
      )}

      {variant === "flat" ? (
        <div className="flex flex-col gap-2">
          {showImages && (
            <CategorizedImagesGrid
              dimensions={dimensions}
              diff={chipDiff}
              hasMore={hasMore}
              images={orderedCombinedData}
              loadMore={loadMore}
              loadMoreRef={loadMoreRef}
              onRemove={onRemove}
              onDoubleClick={handleThumbnailDoubleClick}
              onRightClick={onClassify ? handleThumbnailRightClick : undefined}
            />
          )}
          {renderFooter()}
        </div>
      ) : (
        <Accordion
          className={classes("rounded-md overflow-hidden", {
            "!border-solid !border-2 !border-green-200":
              versus?.categoryChange === "added",
            "!border-solid !border-2 !border-red-500":
              versus?.categoryChange === "removed",
          })}
          elevation={0}
          disableGutters
          expanded={expanded && hasDetails}
          onChange={(_, open) => {
            if (hasDetails) {
              setExpanded(open);
            }
          }}
          style={{
            backgroundColor: bgColor,
            color: textColor,
          }}
        >
          <AccordionSummary
            component="div"
            className={classes({ "cursor-default": !hasDetails })}
            expandIcon={
              hasDetails ? (
                <ExpandMoreIcon
                  style={{
                    color: textColor,
                  }}
                />
              ) : undefined
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <div
              className={classes(
                "flex w-full justify-between items-center gap-2",
                { "pr-2": hasDetails }
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                {versus?.categoryChange === "added" && (
                  <AddBoxIcon className="shrink-0 text-green-200" />
                )}
                {versus?.categoryChange === "removed" && (
                  <DeleteIcon className="shrink-0 text-red-500" />
                )}
                <CategoryRoleDiff roleDiff={roleDiff} />
                {beforeCategoryName !== undefined ? (
                  <ValueChange
                    before={beforeCategoryName}
                    after={afterCategoryName}
                    className="min-w-0"
                  >
                    {(name, { styleChange }) => (
                      <Typography
                        component="span"
                        className={classes(
                          "text-ellipsis whitespace-nowrap overflow-hidden min-w-0",
                          { "line-through": styleChange === "removed" }
                        )}
                      >
                        {name}
                      </Typography>
                    )}
                  </ValueChange>
                ) : (
                  <Typography className="text-ellipsis whitespace-nowrap overflow-hidden min-w-0">
                    {currentName}
                  </Typography>
                )}
                <Typography component="span" className="shrink-0">
                  {`(${imageCount})`}
                </Typography>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {versus && (
                  <DiffSummary
                    added={
                      (chipDiff?.added.length ?? 0) +
                      (versus.shootTimeChangeCounts?.added ?? 0)
                    }
                    removed={
                      (chipDiff?.removed.length ?? 0) +
                      (versus.shootTimeChangeCounts?.removed ?? 0)
                    }
                    className="pl-2"
                  />
                )}
                {hasCategoryMenu && (
                  <>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setMenuAnchor(event.currentTarget);
                      }}
                      sx={{ color: "inherit" }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                    <Menu
                      anchorEl={menuAnchor}
                      open={Boolean(menuAnchor)}
                      onClose={() => setMenuAnchor(undefined)}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {onRequestEditCategory && (
                        <MenuItem
                          onClick={(event) => {
                            event.stopPropagation();
                            setMenuAnchor(undefined);
                            onRequestEditCategory();
                          }}
                        >
                          {t(
                            "components.categoryCollectionProfile.actions.editNameAndType"
                          )}
                        </MenuItem>
                      )}
                      {onRequestDeleteCategory && (
                        <MenuItem
                          onClick={(event) => {
                            event.stopPropagation();
                            setMenuAnchor(undefined);
                            onRequestDeleteCategory();
                          }}
                        >
                          {t("utils.actions.remove")}
                        </MenuItem>
                      )}
                      {onRequestAddToLibrary && (
                        <MenuItem
                          onClick={(event) => {
                            event.stopPropagation();
                            setMenuAnchor(undefined);
                            onRequestAddToLibrary();
                          }}
                        >
                          {t(
                            "components.categoryCollectionProfile.actions.addToCategoryLibrary"
                          )}
                        </MenuItem>
                      )}
                    </Menu>
                  </>
                )}
              </div>
            </div>
          </AccordionSummary>
          <AccordionDetails className="p-1 flex flex-col">
            {showImages && hasChips && (
              <CategorizedImagesGrid
                dimensions={dimensions}
                diff={chipDiff}
                hasMore={hasMore}
                images={orderedCombinedData}
                loadMore={loadMore}
                loadMoreRef={loadMoreRef}
                onRemove={onRemove}
                onDoubleClick={handleThumbnailDoubleClick}
                onRightClick={
                  onClassify ? handleThumbnailRightClick : undefined
                }
                paperClassName={
                  isFooterStackedUnderImages ? "rounded-b-none" : undefined
                }
              />
            )}
            {renderFooter()}
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};

interface DiffSummaryProps {
  added: number;
  removed: number;
  className?: string;
}
const DiffSummary: FC<DiffSummaryProps> = ({ added, removed, className }) => {
  if (added === 0 && removed === 0) {
    return;
  }
  return (
    <div
      className={classes(
        "flex items-center gap-2 px-2 rounded-md bg-darken-700",
        className
      )}
    >
      <DifferenceIcon className="text-white/50 text-base" />
      {added > 0 && (
        <Typography sx={{ color: "success.light" }}>+{added}</Typography>
      )}
      {removed > 0 && (
        <Typography sx={{ color: "error.light" }}>-{removed}</Typography>
      )}
    </div>
  );
};

const CategoryRoleDiff: FC<{ roleDiff?: ValueHistory<Role> }> = ({
  roleDiff,
}) => {
  if (!roleDiff) {
    return;
  }
  if (roleDiff.change === "unchanged") {
    return roleDiff.after === undefined ? undefined : (
      <CategoryRoleDiffIcon role={roleDiff.after} />
    );
  }
  return (
    <ValueChange before={roleDiff.before} after={roleDiff.after}>
      {(value, { styleChange }) => (
        <CategoryRoleDiffIcon role={value} styleChange={styleChange} />
      )}
    </ValueChange>
  );
};

const CategoryRoleDiffIcon: FC<{ role: Role; styleChange?: StyleChange }> = ({
  role,
  styleChange,
}) => {
  const { t } = useTranslation();
  const roleJson = roleToJSON(role);
  const icon = (
    <PlantProfileRoleIcon
      role={role}
      fontSize="small"
      className={classes("shrink-0", {
        "opacity-50": styleChange === "removed",
      })}
    />
  );
  if (roleJson === "ROLE_UNSPECIFIED" || roleJson === "UNRECOGNIZED") {
    return icon;
  }
  return (
    <Tooltip title={t(plantProfileRoleI18nKey(roleJson))} arrow>
      <span className="inline-flex shrink-0 items-center">{icon}</span>
    </Tooltip>
  );
};
